import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type {
  BatteryProposalDto,
  BatteryProposalExtendedWarrantyDto,
  UpdateBatteryProposalExtendedWarrantyDto,
} from "../dto/battery-proposal.dto"
import { batteryProposalService } from "../services/battery-proposal.service"

const isSameSnapshot = (
  previous: BatteryProposalExtendedWarrantyDto | null,
  next: BatteryProposalExtendedWarrantyDto | null,
): boolean =>
  previous?.powurOsId === next?.powurOsId &&
  previous?.name === next?.name &&
  previous?.cost === next?.cost

export default function useUpdateBatteryProposalExtendedWarranty(): UseMutationResult<
  BatteryProposalDto,
  unknown,
  { proposalId: string; body: UpdateBatteryProposalExtendedWarrantyDto }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, body }) =>
      batteryProposalService.updateExtendedWarranty(proposalId, body),
    onSuccess: (proposal, { proposalId }) => {
      const previous = queryClient.getQueryData<BatteryProposalDto>(
        queryKeys.batteryProposal.byId(proposalId),
      )
      queryClient.setQueryData(
        queryKeys.batteryProposal.byId(proposalId),
        proposal,
      )

      // A no-op save (identical ID, name, and cost) changes nothing
      // server-side, so it refreshes nothing here.
      if (
        previous &&
        isSameSnapshot(
          previous.extendedWarranty ?? null,
          proposal.extendedWarranty ?? null,
        )
      ) {
        return
      }

      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.extendedWarranties(proposalId),
      })
      // An effective selection change marks stored Battery System Pricing out
      // of date; the pricing status and the brochure's Financial Summary both
      // report that freshness, so both refetch. Incentive values only move on
      // a successful pricing refresh, which owns that update.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.pricing(proposalId),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.financialSummary(proposalId),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.contract(proposalId),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.financing(proposalId),
      })
    },
  })
}
